import { wordPluralisation } from './words-pluralisation';

import { RANGE_PICKER } from '../consts';
import { RangePickerTranslationModel, SearchEngineTranslationModel } from '../models';

const {
    FLEX_DATES: { DEFAULT, OPTIONS },
} = RANGE_PICKER;

// Instance-based state for remembering user's last selection per picker instance
const lastUserSelections = new Map<string, number>();

/**
 * Get the last user selection for flex dates for a specific instance
 */
export const getLastUserSelection = (instanceId = 'default'): number => {
    return lastUserSelections.get(instanceId) ?? DEFAULT;
};

/**
 * Set the last user selection for flex dates for a specific instance
 */
export const setLastUserSelection = (value: number, instanceId = 'default'): void => {
    lastUserSelections.set(instanceId, value);
};

/**
 * Reset the last user selection to default for a specific instance
 */
export const resetLastUserSelection = (instanceId = 'default'): void => {
    lastUserSelections.delete(instanceId);
};

/**
 * Get initial flex days value based on priority:
 * 1. Last user selection (if > 0)
 * 2. Selected flex days (if defined and > 0)
 * 3. Default flex dates
 */
export const getInitialFlexDaysValue = (
    selectedFlexDays?: number,
    instanceId = 'default',
): number => {
    const lastUserSelection = getLastUserSelection(instanceId);
    return lastUserSelection > DEFAULT
        ? lastUserSelection
        : selectedFlexDays !== undefined && selectedFlexDays > DEFAULT
          ? selectedFlexDays
          : DEFAULT;
};

/**
 * Determine if internal state should sync with external selectedFlexDays
 */
export const shouldSyncWithExternal = (external?: number, internal?: number): boolean => {
    if (external === undefined) return false;
    if (external === internal) return false;

    // Don't override user selection when external resets to default
    const isExternalResetToDefault = external === DEFAULT;
    const hasUserSelection = (internal ?? 0) > DEFAULT;

    return !(isExternalResetToDefault && hasUserSelection);
};

/**
 * Determine if we should preserve user choice when external resets to default
 */
export const shouldPreserveUserChoice = (external?: number, internal?: number): boolean => {
    return external === DEFAULT && (internal ?? 0) > DEFAULT;
};

/**
 * Get label for flex date option
 */
export const getFlexDateLabel = (
    days: number,
    translationData: Required<RangePickerTranslationModel | SearchEngineTranslationModel>,
    shortLabel?: boolean,
): string => {
    if (days === DEFAULT) {
        return 'flexDates' in translationData ? translationData.flexDates.exactDates : '';
    }

    return shortLabel
        ? `${days}d`
        : `${days} ${wordPluralisation(
              days,
              translationData.days.singular,
              translationData.days.plural,
              translationData.days.genitivePlural,
          )}`;
};

/**
 * Generate flex date options with labels
 */
export const generateFlexDateOptions = (translationData: Required<RangePickerTranslationModel>) => {
    return OPTIONS.map(days => ({
        label: getFlexDateLabel(days, translationData),
        days,
    }));
};

/**
 * Handle flex date selection with proper state management
 */
export const handleFlexDateSelection = (
    days: number,
    callbacks: {
        setInternalSelectedFlexDays: (days: number) => void;
        setIsUserChange: (isUserChange: boolean) => void;
        onFlexDateSelect?: (days: number) => void;
    },
    instanceId = 'default',
): void => {
    requestAnimationFrame(() => {
        callbacks.setIsUserChange(true);
        callbacks.setInternalSelectedFlexDays(days);
        setLastUserSelection(days, instanceId);
        callbacks.onFlexDateSelect?.(days);
    });
};
